import ExcelJS from "exceljs";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);

const dateToExcelSerial = (date) => {
  const utc = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
  return Math.round(((utc - EXCEL_EPOCH_UTC) / 86400000) * 1e8) / 1e8;
};

const cellRawValue = (cell, cellDates) => {
  const value = cell?.value;
  if (value == null) {
    return undefined;
  }

  if (value instanceof Date) {
    return cellDates ? value : dateToExcelSerial(value);
  }

  if (typeof value === "object") {
    if (Object.prototype.hasOwnProperty.call(value, "result")) {
      if (value.result instanceof Date) {
        return cellDates ? value.result : dateToExcelSerial(value.result);
      }
      return value.result;
    }
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text || "").join("");
    }
    if (typeof value.text === "string") {
      return value.text;
    }
    if (Object.prototype.hasOwnProperty.call(value, "error")) {
      return undefined;
    }
  }

  return value;
};

const headerLabel = (cell) => {
  if (cell.value == null || cell.value === "") {
    return "__EMPTY";
  }

  try {
    if (cell.text) {
      return String(cell.text);
    }
  } catch {
    // Some calculated cells have no formatted text until the value is read directly.
  }

  const raw = cellRawValue(cell, true);
  return raw == null || raw === "" ? "__EMPTY" : String(raw);
};

const buildHeaders = (sheet) => {
  const headerRow = sheet.getRow(1);
  const columnCount = Math.max(sheet.columnCount, headerRow.cellCount);
  const counts = {};
  const headers = [];

  for (let column = 1; column <= columnCount; column += 1) {
    const base = headerLabel(headerRow.getCell(column));
    const seen = counts[base] || 0;
    let name = base;

    if (!seen) {
      counts[base] = 1;
    } else {
      let counter = seen;
      do {
        name = `${base}_${counter}`;
        counter += 1;
      } while (counts[name]);
      counts[base] = counter;
      counts[name] = 1;
    }

    headers.push(name);
  }

  return headers;
};

export const loadWorkbook = async (data) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(data);
  return workbook;
};

export const sheetToObjects = (sheet, { defval, cellDates = false, blankRows = false } = {}) => {
  if (!sheet || sheet.rowCount < 1) {
    return [];
  }

  const headers = buildHeaders(sheet);
  const rows = [];

  for (let rowNumber = 2; rowNumber <= sheet.rowCount; rowNumber += 1) {
    const excelRow = sheet.getRow(rowNumber);
    const row = {};
    let empty = true;

    Object.defineProperty(row, "__rowNum__", { value: rowNumber - 1, enumerable: false });

    headers.forEach((header, index) => {
      const value = cellRawValue(excelRow.getCell(index + 1), cellDates);
      if (value == null) {
        if (defval !== undefined) {
          row[header] = defval;
        }
        return;
      }

      row[header] = value;
      empty = false;
    });

    if (!empty || blankRows) {
      rows.push(row);
    }
  }

  return rows;
};

export const sheetToMatrix = (sheet) => {
  if (!sheet || sheet.rowCount < 1) {
    return [];
  }

  const columnCount = Math.max(sheet.columnCount, sheet.getRow(1).cellCount);
  const rows = [];

  for (let rowNumber = 1; rowNumber <= sheet.rowCount; rowNumber += 1) {
    const excelRow = sheet.getRow(rowNumber);
    const row = [];

    for (let column = 1; column <= columnCount; column += 1) {
      const value = cellRawValue(excelRow.getCell(column), false);
      row.push(value == null ? "" : value);
    }

    rows.push(row);
  }

  return rows;
};

export const buildXlsxBuffer = async (rows, sheetName, columnWidths) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  if (rows.length > 0) {
    const headers = Object.keys(rows[0]);
    sheet.addRow(headers);
    rows.forEach((row) => {
      sheet.addRow(headers.map((header) => (row[header] == null ? "" : row[header])));
    });
    columnWidths?.forEach((width, index) => {
      sheet.getColumn(index + 1).width = width;
    });
  }

  return workbook.xlsx.writeBuffer();
};

export const downloadXlsx = async (rows, sheetName, fileName, columnWidths) => {
  const buffer = await buildXlsxBuffer(rows, sheetName, columnWidths);
  const blob = new Blob([buffer], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.setAttribute("aria-hidden", "true");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
